import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoginService } from '../../../services/login.service';
import { RanchMemberInviteAcceptResponse, RanchMemberInviteInspectResponse } from '../../../services/interface';

type InviteViewState =
	'loading' |
	'invalid' |
	'expired' |
	'used' |
	'pending_existing' |
	'pending_new' |
	'accepted' |
	'error';

@Component({
	selector: 'app-invite',
	templateUrl: 'invite.page.html',
	styleUrls: ['invite.scss']
})
export class InvitePage implements OnInit, OnDestroy {
	public state: InviteViewState = 'loading';
	public isSaving = false;
	public token: string;
	public invite: RanchMemberInviteInspectResponse;
	public acceptResponse: RanchMemberInviteAcceptResponse;

	private _subscriptions$: Subject<boolean>;

	constructor(
		private _route: ActivatedRoute,
		private _router: Router,
		private _loginService: LoginService) {
	}

	ngOnInit(): void {
		this._subscriptions$ = new Subject();

		this._route.queryParams.pipe(takeUntil(this._subscriptions$)).subscribe(params => {
			this.token = params ? params['token'] : null;
			this._loadInvite();
		});
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	private _loadInvite(): void {
		this.state = 'loading';
		this.invite = null;
		this.acceptResponse = null;

		if (!this.token) {
			this.state = 'invalid';
			return;
		}

		this._loginService.inspectInvite(this.token)
			.then((response: RanchMemberInviteInspectResponse) => {
				this.invite = response;
				this._setStateFromInspect(response);
			})
			.catch(() => {
				this.state = 'error';
			});
	}

	private _setStateFromInspect(response: RanchMemberInviteInspectResponse): void {
		if (!response || !response.Status) {
			this.state = 'invalid';
			return;
		}

		switch ((response.Status || '').toLowerCase()) {
			case 'pending':
				this.state = response.AccountExists ? 'pending_existing' : 'pending_new';
				return;
			case 'expired':
				this.state = 'expired';
				return;
			case 'used':
				this.state = 'used';
				return;
			case 'invalid':
			default:
				this.state = 'invalid';
				return;
		}
	}

	public goToRegister(): void {
		let navigationExtras: NavigationExtras;

		if (!this.invite || !this.invite.InvitedEmail) {
			this._router.navigate(['register']);
			return;
		}

		navigationExtras = {
			queryParams: {
				email: this.invite.InvitedEmail
			}
		};

		this._router.navigate(['register'], navigationExtras);
	}

	public goToLogin(): void {
		this._router.navigate(['login']);
	}

	public acceptInvite(): void {
		if (!this.token || this.isSaving) {
			return;
		}

		this.isSaving = true;
		this.acceptResponse = null;

		this._loginService.acceptInvite(this.token)
			.then((response: RanchMemberInviteAcceptResponse) => {
				this.acceptResponse = response;

				if (response && response.Status && response.Status.toLowerCase() === 'accepted') {
					this.state = 'accepted';
				} else if (response && response.Status && response.Status.toLowerCase() === 'registration_required') {
					this.state = 'pending_new';
				} else if (response && response.Status) {
					// Handle used/expired/invalid surfaced by accept endpoint.
					this.state = response.Status.toLowerCase() as InviteViewState;
				} else {
					this.state = 'error';
				}

				this.isSaving = false;
			})
			.catch(() => {
				this.state = 'error';
				this.isSaving = false;
			})
	}
}

